import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { HttpService } from '@core/services';
import { PaceTaskHttpService } from '@core/services/http';
import { PaceTaskTabScreens } from '@models/enums';
import { PaceTaskScreen } from '@models/interfaces';
import { PaceTaskSelectors } from '@store/selectors';
import { prefetchedScreens } from '../../pace-task-prefetch-registry';

export interface UseScreenPaceTasksResult {
  data: PaceTaskScreen[];
  isLoading: boolean;
  hasError: boolean;
  invalidate: () => void;
}

export const useScreenPaceTasks = (
  screen: PaceTaskTabScreens,
  isActive: boolean,
): UseScreenPaceTasksResult => {
  const paceTaskService = useMemo(
    () => HttpService.getHttpRequests(PaceTaskHttpService),
    [],
  );
  const data = useSelector(PaceTaskSelectors.getScreenData(screen));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const lastFetchedScreen = useRef<string | null>(null);
  const prefetchedRef = useRef<boolean>(false);
  const dataRef = useRef(data);
  const [fetchTrigger, setFetchTrigger] = useState<number>(0);

  dataRef.current = data;

  const invalidate = useCallback((): void => {
    lastFetchedScreen.current = null;
    prefetchedScreens.delete(screen);
    setFetchTrigger((t: number): number => t + 1);
  }, [screen]);

  useEffect(() => {
    if (!isActive || lastFetchedScreen.current === screen) {
      return;
    }

    lastFetchedScreen.current = screen;

    if (dataRef.current.length > 0 || prefetchedScreens.has(screen)) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    paceTaskService
      .getScreenPaceTasks(screen)
      .then((): void => {
        setIsLoading(false);

        if (!prefetchedRef.current) {
          prefetchedRef.current = true;

          Object.values(PaceTaskTabScreens).forEach(
            (s: PaceTaskTabScreens): void => {
              if (s !== screen && !prefetchedScreens.has(s)) {
                prefetchedScreens.add(s);
                paceTaskService.getScreenPaceTasks(s);
              }
            },
          );
        }
      })
      .catch((): void => {
        setHasError(true);
        setIsLoading(false);
        lastFetchedScreen.current = null;
      });
  }, [isActive, screen, fetchTrigger]);

  return { data, isLoading, hasError, invalidate };
};
